/** @jsxImportSource @emotion/react */

import {
  Checkbox,
  css,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import Table from "components/common/Table";
import { Store } from "models/business.model";
import { SELECT_STORE_COLS } from "./column.data";
import { useEffect, useRef, useState } from "react";
import BaseInput from "components/common/Input/Base";
import BaseButton from "components/common/Button/Base";
import { PALETTE } from "styles/base/variables";
import {
  MatchStoreStatementRequest,
  putBankStatementMatchingStore,
  putRecordsMatchingStore,
} from "pages/remotes";
import AlertDialog, { AlertData } from "components/common/Dialog/Alert";
import { Close } from "@mui/icons-material";

export interface SelectStoreDialogSettings {
  storeId: string | null;
  dataIds: string[];
  isCancel?: boolean;
}

interface SelectStoreDialogProps {
  isOpen: boolean;
  onClose: () => void;
  settings: SelectStoreDialogSettings;
  storeList: Store[];
  businessId?: string;
  successCallback?: () => void;
}

function SelectStoreDialog({
  isOpen,
  settings,
  onClose,
  storeList,
  businessId,
  successCallback,
}: SelectStoreDialogProps) {
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [holdingStore, setHoldingStore] = useState<Store | null>(null);
  const [filteredStoreList, setFilteredStoreList] = useState<Store[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isBatchChange, setIsBatchChange] = useState(false);
  const [alertDialogData, setAlertDialogData] = useState<AlertData>({
    message: "",
  });

  useEffect(() => {
    setFilteredStoreList(storeList);

    const store = storeList.find((store) => store.id === settings.storeId);
    setSelectedStore(store || null);
  }, [storeList, settings.storeId]);

  const resetStatus = () => {
    setIsLoading(false);
    setIsAlertOpen(false);
    setIsBatchChange(false);
    setSelectedStore(null);
    setHoldingStore(null);
    setFilteredStoreList(storeList);
    setAlertDialogData({ message: "" });
  };

  const onCloseDialog = () => {
    onClose();
    resetStatus();
  };

  const selectStoreHandler = (store: Store) => {
    setSelectedStore(store);
  };

  const searchStoreHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const keyword = e.target.value;

    if (!keyword) {
      setFilteredStoreList(storeList);
      return;
    }

    const filtered = storeList.filter(
      (store) =>
        store.name.includes(keyword) ||
        store.ceoName?.includes(keyword) ||
        store.regNumber?.includes(keyword)
    );
    setFilteredStoreList(filtered);
  };

  const keyDownHanlder = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation(); // esc 클릭시 다이얼로그 닫힘 방지
    if (e.nativeEvent.isComposing) return; // IME 입력 중인 경우 무시 (한글 조합으로 두번 호출 무시)

    const event = e as React.KeyboardEvent<HTMLInputElement>;
    const eventKey = event.key;
    const eventTarget = event.target as HTMLInputElement;
    const tgtValue = eventTarget?.value;

    if (eventKey === "ArrowDown" || eventKey === "ArrowUp") {
      if (filteredStoreList.length > 0) {
        let currentIndex = -1;
        let nextIndex = 0;
        if (holdingStore) {
          currentIndex = filteredStoreList.findIndex(
            (store) => store.id === holdingStore.id
          );

          if (eventKey === "ArrowDown") {
            nextIndex = currentIndex + 1;
          } else if (eventKey === "ArrowUp") {
            nextIndex = currentIndex - 1;
          }
        }

        console.log("nextIndex", nextIndex, filteredStoreList.length);

        if (nextIndex >= 0 && nextIndex < filteredStoreList.length) {
          setHoldingStore(filteredStoreList[nextIndex]);
        } else {
          setHoldingStore(null);
        }
      }
    } else if (eventKey === "Enter") {
      if (holdingStore) {
        selectStoreHandler(holdingStore);
        return;
      }
      if (filteredStoreList.length > 0) {
        if (tgtValue) {
          const firstMatch = filteredStoreList.find(
            (store) =>
              store.name.includes(tgtValue) ||
              store.ceoName?.includes(tgtValue) ||
              store.regNumber?.includes(tgtValue)
          );

          if (firstMatch) {
            selectStoreHandler(firstMatch);
          }
        }
      }
    } else if (eventKey === "Escape") {
      if (searchInputRef.current) searchInputRef.current.value = "";
      setFilteredStoreList(storeList);
      setHoldingStore(null);
      setSelectedStore(null);
    } else {
      setHoldingStore(null);
    }
  };

  const onSelect = () => {
    if (settings.dataIds?.length === 0) {
      setAlertDialogData({
        message: "선택된 데이터 ID가 없습니다.",
      });
      setIsAlertOpen(true);
      return;
    }

    if (!selectedStore) {
      setAlertDialogData({
        message: "거래처를 선택해주세요.",
      });
      setIsAlertOpen(true);
      return;
    }

    setIsLoading(true);
    if (settings.dataIds.length === 1) {
      const reqData: MatchStoreStatementRequest = {
        storeId: settings.isCancel ? null : selectedStore.id,
        modifyPastData: isBatchChange,
        registerKeyword: isBatchChange,
      };
      const dataId = settings.dataIds[0];
      putBankStatementMatchingStore(dataId, reqData)
        .then(() => {
          setIsLoading(false);
          if (successCallback) successCallback();
          onCloseDialog();
          resetStatus();
        })
        .catch((error) => {
          setIsLoading(false);
          setIsAlertOpen(true);
        });
    } else {
      if (!businessId) {
        setAlertDialogData({
          message: "업체 ID가 없습니다.",
        });
        setIsAlertOpen(true);
        setIsLoading(false);
        return;
      }

      putRecordsMatchingStore(
        "bank",
        businessId,
        selectedStore.id,
        settings.dataIds
      )
        .then(() => {
          setIsLoading(false);
          if (successCallback) successCallback();
          onCloseDialog();
          resetStatus();
        })
        .catch((error) => {
          setIsLoading(false);
          setIsAlertOpen(true);
        });
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onCloseDialog}
      maxWidth={settings.isCancel ? "sm" : "md"}
      fullWidth
    >
      <DialogTitle>거래처 {settings.isCancel ? "해제" : "선택"}</DialogTitle>
      <DialogContent>
        {settings.isCancel ? (
          <Grid>
            <strong>{selectedStore?.name}</strong> 거래처를 해제합니다.
          </Grid>
        ) : (
          <Grid display="flex" flexDirection="column" gap={2}>
            <BaseInput
              ref={searchInputRef}
              placeholder="거래처 검색 (거래처명, 등록번호, 대표자명)"
              css={css`
                width: 100%;
              `}
              onKeyDown={keyDownHanlder}
              onInput={searchStoreHandler}
            />
            <Table
              columns={SELECT_STORE_COLS}
              dataList={filteredStoreList}
              highlightRowId={selectedStore?.id}
              holdingRowId={holdingStore?.id}
              minWidth={600}
              minHeight="calc(100vh - 360px)"
              clickRow={selectStoreHandler}
            />
            <Grid
              height={20}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid>
                {settings.dataIds.length === 1 && (
                  <FormControlLabel
                    label={
                      <Typography fontSize="14px" fontWeight={500}>
                        일괄 변경
                      </Typography>
                    }
                    control={
                      <Checkbox
                        size="small"
                        checked={isBatchChange}
                        onChange={() => setIsBatchChange(!isBatchChange)}
                      />
                    }
                  />
                )}
              </Grid>
              <Grid display="flex" alignItems="center">
                {selectedStore ? (
                  <>
                    <strong>{selectedStore.name || "(거래처명 없음)"}</strong>
                    <IconButton
                      sx={{ ml: -0.75 }}
                      onClick={() => setSelectedStore(null)}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  "거래처를 선택해주세요"
                )}
              </Grid>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Grid
          width="100%"
          display="flex"
          marginX={2}
          paddingTop={2}
          paddingBottom={1}
          justifyContent="flex-end"
          gap={1}
          borderTop={"1px solid " + PALETTE.border}
        >
          <BaseButton
            variant="outlined"
            color={PALETTE.primary}
            onClick={onCloseDialog}
            style={{ marginRight: 8 }}
          >
            닫기
          </BaseButton>
          {settings.isCancel ? (
            <BaseButton
              variant="contained"
              color={PALETTE.primary}
              onClick={onSelect}
              disabled={isLoading || !selectedStore}
            >
              해제
            </BaseButton>
          ) : (
            <Tooltip
              title={
                isLoading
                  ? "로딩 중입니다."
                  : !selectedStore
                  ? "거래처를 선택해주세요."
                  : selectedStore.id === settings.storeId
                  ? "기존에 선택된 거래처입니다."
                  : ""
              }
              placement="top"
            >
              <div>
                <BaseButton
                  variant="contained"
                  color={PALETTE.primary}
                  onClick={onSelect}
                  disabled={
                    isLoading ||
                    !selectedStore ||
                    selectedStore.id === settings.storeId
                  }
                >
                  선택
                </BaseButton>
              </div>
            </Tooltip>
          )}
        </Grid>
      </DialogActions>
      <AlertDialog
        isOpen={isAlertOpen}
        setIsOpen={setIsAlertOpen}
        alertData={alertDialogData}
      />
    </Dialog>
  );
}

export default SelectStoreDialog;
